import { useCallback, useState } from 'react';
import { useStorageSync } from '../useStorageSync';

const STORAGE_PREFIX = 'itow.help.';

/**
 * Read/write whether a help message has been dismissed by the current user.
 * Stored under localStorage key `itow.help.{persistKey}.dismissed`.
 *
 * When persistKey is undefined or empty, returns [false, no-op] so the
 * caller can always pass it down without an outer null check — the
 * Alert simply renders permanently in that case.
 *
 * Slice 663: subscribes to `itow-help-changed` (same-tab restore-all from
 * PreferencesPage) + `storage` (cross-tab) so dismissals + restorations
 * propagate to mounted HelpAlerts without a page refresh.
 */
export function useDismissedHelp(
  persistKey?: string,
): [boolean, () => void] {
  const storageKey = persistKey ? `${STORAGE_PREFIX}${persistKey}.dismissed` : null;

  const [dismissed, setDismissedState] = useState<boolean>(() => {
    if (storageKey === null) return false;
    try {
      return localStorage.getItem(storageKey) === 'true';
    } catch {
      // localStorage may be unavailable (private mode, quota); fail open.
      return false;
    }
  });

  const refresh = useCallback(() => {
    if (storageKey === null) return;
    try {
      setDismissedState(localStorage.getItem(storageKey) === 'true');
    } catch {
      // ignore
    }
  }, [storageKey]);
  useStorageSync('itow-help-changed', refresh);

  const setDismissed = useCallback(() => {
    if (storageKey === null) return;
    setDismissedState(true);
    try {
      localStorage.setItem(storageKey, 'true');
    } catch {
      // Same fail-open posture — don't crash on storage failure.
    }
    window.dispatchEvent(new Event('itow-help-changed'));
  }, [storageKey]);

  return [dismissed, setDismissed];
}
